"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePluralTranslator, useTranslator } from "@/lib/translation-context";
import dynamic from "next/dynamic";

import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { approxMoney, tierUnitNoun } from "@tripcart/shared/format";
import { localDateStr } from "@tripcart/shared";
import { cx } from "@tripcart/traveler-ui/cx";
import {
  Users,
  CircleCheck,
  ChevronDown,
  AlertCircle,
  MessageCircle,
  Send,
  Clock,
  Mail,
  Phone,
  TicketPercent,
  ExternalLink,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
} from "@tripcart/traveler-ui/components/alert";
import { Button } from "@tripcart/traveler-ui/components/button";
import { LazyPopover } from "@/components/lazy-floating/popover.client";
import { GroupTierTable } from "./_group-tiers";
import { FieldOverlay } from "./_booking-sidebar/_field-overlay.client";

import { MerchFlag } from "@/components/merch-flag";
import { StarRow } from "./_star-row";
import { PriceFigure } from "./_price-figure";
import { useFormatDate, useLocaleTag } from "@/lib/datetime-format-context";
import {
  useCurrencyFormat,
  useFormatMoney,
} from "@/lib/currency-format-context";
import type { TravelersState } from "@/lib/cart/types";
import { to24h } from "@/lib/cart/availability-client";
import type {
  DepartureDto,
  PackageDto,
  ExperienceDto,
  OnRequestConfigDto,
} from "@tripcart/shared";
import type { QuestionDialogProps } from "./_question-dialog.client";
import { resolveCategoryUnitPriceMinor } from "@tripcart/shared/pricing/booking-math";

import {
  chargeBasisLabel,
  describeUnit,
  paxFor,
  resolveGlobalCapacityCap,
  stepBlockedBy,
} from "./_capacity";
import { resolveBookingConfigState } from "./_booking-config";
import { resolveBookingPricing } from "./_active-pricing";
import {
  canIncrementCategory,
  clampToUnitCaps,
  decrementFloor,
  reduceTravelerUpdate,
} from "./_traveler-picker";
import { useUnitCaps } from "./_use-unit-caps";
import { resolveCalendarPrices } from "./_calendar-pricing";
import {
  fetchAvailabilityPage,
  type AvailabilityPage,
} from "@/lib/cart/availability-client";
import { AskButton } from "./_booking-sidebar/_ask-button";
import { resolvePriceUnitLine } from "./_price-unit-line";
import { DateField } from "./_booking-sidebar/_date-field.client";
import { NotConfiguredCard } from "./_booking-sidebar/_not-configured-card";
import { Trust } from "./_booking-sidebar/_trust";
import { TravelerRow } from "./_booking-sidebar/_traveler-row";
import {
  TravelersSummary,
  type TravelerSummaryRow,
} from "./_booking-sidebar/_travelers-summary.client";
import { LazyTooltip } from "@/components/lazy-floating/tooltip.client";
import { mergeTriggerProps } from "@/components/lazy-floating/trigger-props";

import styles from "./_booking-sidebar.module.css";

// 60 days per forward page — keeps the response small (< 100 departures
// for a daily-running experience) and matches the 2-month side-by-side
// desktop calendar's visible range, so one fetch typically covers the
// next "Next month" click too.
const NEXT_PAGE_DAYS = 60;

// Auto-seek (see `seekForward` below) jumps in much larger, max-span
// strides — finding a date a year out 60 days at a time would take a dozen
// round trips before the calendar shows anything useful.
const SEEK_PAGE_DAYS = 365;
// Backstop iteration cap (~4 years of jumps) — the API's own ~3-year
// availability horizon should exhaust `hasMoreAfter` well before this.
const SEEK_MAX_ITERATIONS = 4;

/** Up to this many categories, the rows sit in the rail itself; more go
 *  behind the picker so the rail stays short. */
const INLINE_CATEGORY_LIMIT = 3;

/** One step's price for a picker row, at the row's current count (one when
 *  empty). `null` when a tiered category has no band for that count, because
 *  the shared resolver would fall through to 0 and the row would read "Free". */
function rowUnitPriceMinor(
  cat: PackageDto["categories"][number],
  count: number,
): number | null {
  const quantity = Math.max(count, 1);
  if (cat.strategy === "tiered" && cat.tiers?.length) {
    const hasBand = cat.tiers.some(
      (t) => quantity >= t.from && (t.to === null || quantity <= t.to),
    );
    if (!hasBand && !cat.flat) return null;
  }
  return resolveCategoryUnitPriceMinor(cat, quantity);
}

/** Add `days` to a YYYY-MM-DD string and return YYYY-MM-DD. Operates in
 *  UTC so the result is timezone-stable. */
function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Last YYYY-MM-DD visible in the calendar when it shows `monthsAhead`
 *  months starting at `month`. e.g. May + June → June 30. */
function lastVisibleDayIso(month: Date, monthsAhead: number): string {
  const last = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + monthsAhead, 0),
  );
  return last.toISOString().slice(0, 10);
}

/** Format the operator's wire-format age group into a traveler-row subtitle.
 *  Handles the canonical shapes ("18-99", "65+", "0-3" = max-only) and the
 *  historical "N-M yrs" / "N+ years" variants, mirroring the operator's
 *  `ageGroupToRange` parser. Returns null when no range is set — never a
 *  doubled "Age 13+ years". */
function formatAgeRange(group: string | null | undefined): string | null {
  if (!group) return null;
  const range = group.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    return min === 0 ? `Age ${max} and younger` : `Age ${min}–${max}`;
  }
  const open = group.match(/(\d+)\s*\+/);
  if (open) return `Age ${Number(open[1])}+`;
  // Unrecognised free text — surface it verbatim rather than hide it.
  return `Age ${group}`;
}

// Phase 1D (perf/lighthouse-100): QuestionDialog ships ~20 KB of
// Dialog + Input + Textarea + Label + RadioGroup primitives that most
// page views never need. Lazy-load it via next/dynamic; the
// `prefetchQuestionDialog` helper warms the chunk on hover/focus of
// any "Ask a question" button so the click feels instant.
const QuestionDialog = dynamic<QuestionDialogProps>(
  () =>
    import("./_question-dialog.client").then((m) => ({
      default: m.QuestionDialog,
    })),
  { ssr: false },
);

function prefetchQuestionDialog(): void {
  void import("./_question-dialog.client");
}

type SeasonDto = NonNullable<ExperienceDto["seasons"]>[number];

interface BookingSidebarProps {
  travelers: TravelersState;
  setTravelers: React.Dispatch<React.SetStateAction<TravelersState>>;
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  onCheckAvailability: () => void;
  showError: boolean;
  /** Initial server-rendered departures slice. The sidebar lifts this
   *  into state and extends it client-side as the user navigates past
   *  the loaded window. */
  departures: DepartureDto[] | null;
  /** Experience slug — used by the client-side fetcher to page the
   *  calendar forward. When absent (e.g. demo fixture rendering), the
   *  calendar runs in single-window mode. */
  experienceSlug?: string;
  /** Asks traveler-api how many of each rental the picked date has left. */
  experienceId?: string;
  /** Tour name and length, named back to the traveller in the "Ask about this
   *  tour" dialog so they can see what the question will be attached to. */
  experienceName?: string;
  durationDays?: number | null;
  askQuestionHeading?: string;
  /** YYYY-MM-DD of the last day the SSR slice evaluated. The client
   *  uses this as the anchor for forward paging. */
  initialWindowEnd?: string | null;
  /** Whether the SSR slice indicates more departures exist past
   *  `initialWindowEnd`. When false, the calendar stops paging. */
  initialHasMoreAfter?: boolean;
  priceFrom?: { amount: number; currencyCode: string };
  packages?: PackageDto[];
  seasons?: SeasonDto[];
  pricingMode?: string;
  onRequest?: OnRequestConfigDto;
  /** Controlled from the parent so every "Request a Quote" touchpoint on the
   *  page (departure rows, package cards, the mobile sticky bar) opens this
   *  SAME dialog instance, not a second one. */
  quoteFormDialogOpen: boolean;
  setQuoteFormDialogOpen: (open: boolean) => void;
  selectedPackageId?: string;
  onPackageChange?: (id: string) => void;
  /** True when any package prices a category by group size — surfaces an
   *  honest "lower price for larger groups" hint. */
  hasGroupPricing?: boolean;
  /** The operator's payment policy for this experience. Drives the deposit /
   *  balance-due line. Absent when no policy is configured — the line then
   *  renders nothing rather than promising a default the operator never set. */
  payment?: { depositPercent: number; balanceDueDaysBefore: number };
  /** Misc listing details (AC-20), operator-authored — rendered verbatim near
   *  the price. `badge` is a pill (e.g. "Best seller"), `soldAs` a label
   *  (e.g. "Private tour"), `pricingHighlights` a what's-included checklist. */
  badge?: string;
  soldAs?: string;
  pricingHighlights?: string[];
  /** Aggregate review score for the rail's star row. Absent, or a zero count,
   *  means nobody has reviewed this tour — the row is then omitted rather than
   *  drawn empty, which reads as a zero rating. */
  rating?: { average: number; count: number };
  /** Reports the sidebar's live departures (SSR seed + every paged-forward
   *  slice) up to the parent so the package cards read the SAME array. Without
   *  this, a date the calendar paged in but the cards never loaded shows as
   *  "not available" in the cards. */
  onDeparturesChange?: (departures: DepartureDto[] | null) => void;
  /** Reports the sidebar's live `hasMoreAfter` up to the parent so the
   *  mobile sticky bar's not-configured check (which can't see the
   *  sidebar's internal paging state) agrees with the sidebar's own. */
  onHasMoreAfterChange?: (hasMoreAfter: boolean) => void;
}

export function BookingSidebar({
  travelers,
  setTravelers,
  selectedDate,
  setSelectedDate,
  onCheckAvailability,
  showError,
  departures: initialDepartures,
  experienceSlug,
  experienceId,
  experienceName,
  durationDays,
  askQuestionHeading,
  initialWindowEnd,
  initialHasMoreAfter,
  priceFrom: _priceFrom,
  packages,
  seasons,
  pricingMode,
  onRequest,
  quoteFormDialogOpen: isQuoteFormDialogOpen,
  setQuoteFormDialogOpen: setIsQuoteFormDialogOpen,
  selectedPackageId,
  hasGroupPricing,
  payment,
  badge,
  soldAs,
  pricingHighlights,
  rating,
  onDeparturesChange,
  onHasMoreAfterChange,
}: BookingSidebarProps) {
  const t = useTranslator();
  const tn = usePluralTranslator();
  const formatMoney = useFormatMoney();
  const localeTag = useLocaleTag();
  const currencySettings = useCurrencyFormat();
  const formatDate = useFormatDate();
  // Lift the SSR-seeded departures into client state so we can extend it
  // forward as the user navigates calendar months. The initial value
  // matches what the server rendered, so first paint is identical.
  const [departures, setDepartures] = useState<DepartureDto[] | null>(
    initialDepartures ?? null,
  );
  const [loadedEndDate, setLoadedEndDate] = useState<string | null>(
    initialWindowEnd ?? null,
  );
  const [hasMoreAfter, setHasMoreAfter] = useState<boolean>(
    initialHasMoreAfter ?? false,
  );
  // Mirror the live departures up to the parent so the package cards read the
  // exact same (paged-forward) array the calendar does — fires only when the
  // array reference actually changes (initial seed + each forward page).
  useEffect(() => {
    onDeparturesChange?.(departures);
  }, [departures, onDeparturesChange]);
  // Mirror hasMoreAfter up to the parent so the mobile sticky bar's
  // not-configured check agrees with the sidebar's own (see
  // resolveBookingConfigState's hasMoreAfter param).
  useEffect(() => {
    onHasMoreAfterChange?.(hasMoreAfter);
  }, [hasMoreAfter, onHasMoreAfterChange]);
  // Concurrent-fetch guard — `onMonthChange` can fire rapidly when the
  // user clicks the chevron multiple times before the previous response
  // lands. The ref avoids re-rendering for state we only read inside an
  // effect.
  const isLoadingMoreRef = useRef(false);

  // One chain, shared with the pinned mobile bar — see `_active-pricing.ts`.
  // The bar needs these numbers before this component is mounted at all on a
  // phone, so the maths cannot live in here any more.
  const { rawPkg, categories, totals } = resolveBookingPricing({
    packages,
    seasons,
    selectedDate,
    selectedPackageId,
    travelers,
  });
  // AC-10: switching a tour TO on-request never deletes its packages — they
  // just stop being editable from the Pricing step (product.md "How it
  // works"). When a package survives the switch, the traveler can still
  // browse its departure dates below (price hidden on the card there); a
  // tour with none has nothing to browse, so the sidebar shows only the
  // quote box. See the `.body` render below.
  const hasDepartureDates = (packages?.length ?? 0) > 0;
  // The traveler picker is capped by the *global* max across the window so a
  // visitor can express up to the largest seasonal `capacityOverride` even
  // before they've picked a date.
  //
  // Per-date conflicts are NOT handled here. An earlier version of this comment
  // claimed `updateTraveler` silently clears the date when the party exceeds the
  // picked departure's `spotsLeft` — it never did, and believing it hid the fact
  // that the only remaining-seat check on this page lives on the package cards
  // (`resolvePackageBookingGate`), with the reserve path's 409 as the floor.
  const pickerCap = resolveGlobalCapacityCap(rawPkg, departures);
  const unitCaps = useUnitCaps({
    experienceId,
    packageId: rawPkg?.id,
    date: selectedDate,
    startTimes: startTimesOn(departures, selectedDate, rawPkg?.id),
    categories,
  });
  // A smaller date (or a unit booked since) lowers a rental already counted.
  useEffect(() => {
    setTravelers((prev) => clampToUnitCaps(prev, unitCaps));
  }, [unitCaps, setTravelers]);

  // Per-date prices for the calendar. Resolved here, not inside the lazy
  // calendar chunk, because the rounding disclosure below the grid needs the
  // same map — computing it twice would let the two disagree.
  const calendarPrices = useMemo(
    () => resolveCalendarPrices({ departures, packages, seasons }),
    [departures, packages, seasons],
  );

  // "Prices may be rounded" is a claim about what is on screen, so it appears
  // only when a price actually lost precision — always-on would be noise on
  // the majority of tours, whose prices render in full. Scoped to the whole
  // loaded window rather than the visible month so it doesn't blink in and out
  // as the traveler pages between months.
  const hasRoundedPrice = useMemo(() => {
    for (const price of calendarPrices.values()) {
      if (price.amountMinor === 0) continue;
      if (!approxMoney(price.amountMinor, currencySettings).isExact)
        return true;
    }
    return false;
  }, [calendarPrices, currencySettings]);

  /**
   * GYG/Viator render two months side-by-side on desktop (lets users compare
   * June vs July without paginating). Below that we collapse back to one. The
   * hook is SSR-safe — false until hydration.
   *
   * The cut-off is 954px, not the 640px this used to ask for. Measured on this
   * screen, not carried over: `CALENDAR_SURFACE_CLASS` gives this calendar
   * 60px cells, so one month grid is 420px, and two of them plus the 32px
   * `.rdp-months` gap and the popover's own insets come to a natural popover
   * width of 938px — which `max-w-[calc(100vw-1rem)]` only allows from 954px
   * of viewport up.
   *
   * Between 640 and 953 the second month did not shrink, it broke. At 900x900
   * November's Saturday column and the next-month arrow rendered outside the
   * popover's own card. Below Tailwind's `md` the two months stopped sitting
   * side by side at all and stacked: at 760x700 the popover was 486x1227, flew
   * upward off the top of the window, and left 806px of itself — all of
   * October — above the viewport.
   *
   * The cart's twin of this line reads 820px. That is the same defect and a
   * different number on purpose: its calendar has 52px cells, so its months are
   * 364px wide and two of them need less room than two of these.
   */
  const fitsTwoMonths = useMediaQuery("(min-width: 954px)");
  const calendarMonths = fitsTwoMonths ? 2 : 1;
  const [isTravelersOpen, setIsTravelersOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // The quote box's "Open inquiry form" button opens the operator's chosen
  // form (AC-09) — a different dialog session from the generic Ask a
  // Question one above, since it is a different (pre-resolved) form. Its
  // open state is a controlled prop (`quoteFormDialogOpen`), not local
  // state — every "Request a Quote" touchpoint on the page opens the SAME
  // dialog instance this component renders.
  const [isQuoteFormSubmitting, setIsQuoteFormSubmitting] = useState(false);
  const [isQuoteFormSubmitted, setIsQuoteFormSubmitted] = useState(false);

  useEffect(() => {
    if (showError && !selectedDate) setIsCalendarOpen(true);
  }, [showError, selectedDate]);

  // Merges a fetched page into departures + pagination state. Shared by
  // `pageForward` (60-day, user-driven) and `seekForward` (365-day,
  // auto-triggered) below — both need the identical dedup-by-id merge.
  const mergePage = useCallback((page: AvailabilityPage) => {
    setDepartures((prev) => {
      const merged = new Map<string, DepartureDto>();
      for (const d of prev ?? []) merged.set(d.id, d);
      for (const d of page.departures) merged.set(d.id, d);
      return Array.from(merged.values()).sort((a, b) =>
        a.date.localeCompare(b.date, "en"),
      );
    });
    setLoadedEndDate(page.window.windowEnd);
    setHasMoreAfter(page.window.hasMoreAfter);
  }, []);

  // Fetch the next 60-day slice and merge it into the existing
  // departures + pagination state. No-op if there's nothing more to load
  // or a fetch is already in flight (concurrent-fetch guard).
  const pageForward = useCallback(async () => {
    if (!experienceSlug) return;
    if (!hasMoreAfter || !loadedEndDate) return;
    if (isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    const startDate = addDaysIso(loadedEndDate, 1);
    const endDate = addDaysIso(loadedEndDate, NEXT_PAGE_DAYS);
    const result = await fetchAvailabilityPage(experienceSlug, {
      startDate,
      endDate,
    });
    isLoadingMoreRef.current = false;
    if (!result.ok) return;
    mergePage(result.page);
  }, [experienceSlug, hasMoreAfter, loadedEndDate, mergePage]);

  // The initial fetch only loads a short window (traveler-api's own default,
  // currently 90 days) to keep first paint fast. A run period with no end
  // date — or one scheduled a year out — has real departures past that
  // window, and three surfaces all read this same `departures` state: the
  // calendar's bookable days, the package cards' spotsLeft, and the "Dates &
  // prices" year/month tabs (lifted up via `onDeparturesChange`). Left alone,
  // all three silently stop at the initial window's edge even though the
  // operator's schedule continues — which is exactly the mismatch a visitor
  // sees against the operator-side preview (that preview computes the whole
  // multi-year schedule client-side, for free; this one is a paid network
  // fetch per page, so it has to be fetched incrementally instead).
  //
  // So once the initial load settles, keep paging forward in the background
  // — large (year-long) strides, well after first paint — until the API says
  // there's nothing more (`hasMoreAfter: false`) or the iteration backstop is
  // hit. Using local loop variables (not the React state setters) so each
  // iteration reads the page that was JUST fetched, not last-render's stale
  // state.
  const seekForward = useCallback(async () => {
    if (!experienceSlug) return;
    let end = loadedEndDate;
    let more = hasMoreAfter;
    for (let i = 0; i < SEEK_MAX_ITERATIONS && more && end; i++) {
      if (isLoadingMoreRef.current) return;
      isLoadingMoreRef.current = true;
      const startDate = addDaysIso(end, 1);
      const endDate = addDaysIso(end, SEEK_PAGE_DAYS);
      const result = await fetchAvailabilityPage(experienceSlug, {
        startDate,
        endDate,
      });
      isLoadingMoreRef.current = false;
      if (!result.ok) return;
      mergePage(result.page);
      end = result.page.window.windowEnd;
      more = result.page.window.hasMoreAfter;
    }
  }, [experienceSlug, loadedEndDate, hasMoreAfter, mergePage]);

  // Fires the background fill exactly once per mount, as soon as the initial
  // window is known (not `null`, which means "still loading" — see
  // `resolveBookingConfigState`) and the API says more departures exist
  // beyond it. Runs whether or not the initial window already has
  // departures in it — a tour already running has just as much schedule
  // past the window as one that hasn't started yet.
  const hasSeekedRef = useRef(false);
  useEffect(() => {
    if (hasSeekedRef.current) return;
    if (departures === null || !hasMoreAfter) return;
    hasSeekedRef.current = true;
    void seekForward();
  }, [departures, hasMoreAfter, seekForward]);

  // Page the calendar forward when the user navigates past the loaded
  // window. Cheap boundary check first; `pageForward` then does the
  // actual fetch (it's also called preemptively on calendar open).
  const handleCalendarMonthChange = useCallback(
    async (month: Date) => {
      if (!loadedEndDate) return;
      const lastVisible = lastVisibleDayIso(month, calendarMonths);
      if (lastVisible <= loadedEndDate) return;
      await pageForward();
    },
    [loadedEndDate, calendarMonths, pageForward],
  );

  // Prefetch the next page when the calendar opens so the first chevron
  // click past the initial window shows departures immediately instead
  // of waiting for a fetch round-trip. Fires once per open: pageForward
  // bails out if loaded data already covers the next page.
  useEffect(() => {
    if (!isCalendarOpen) return;
    void pageForward();
  }, [isCalendarOpen, pageForward]);

  // Calendar chunk (react-day-picker + date-fns, ~50 KB gz) is prefetched
  // on user intent only — onMouseEnter / onFocus of the date field (see
  // the input handlers below). The previous unconditional idle-time
  // prefetch downloaded the chunk for every visitor including the ~70%
  // who never open the calendar, costing them bandwidth and TBT for
  // nothing.

  // Head-count (not unit-count) across all categories, so per-unit categories
  // (e.g. Couple, Cabin) contribute their full fan-out to the "Total Travelers"
  // pill and the capacity guard.
  //
  // Read off `totals` rather than calling `sumPax` again. It is the same
  // function over the same two inputs, so a second call cannot disagree today —
  // but "two places deriving one number" is the shape of every pricing bug this
  // page has shipped, and `_active-pricing.ts` exists to have exactly one. The
  // `?? 0` covers the no-priced-category case, where `totals` is null and
  // `sumPax([], travelers)` was 0 anyway.
  const totalTravelers = totals?.headCount ?? 0;

  // Translate the pure hint from `describeUnit` for the row's meta line, so a
  // Couple reads "2 people each" rather than leaving the traveller to work out
  // why the party jumped by two.
  const unitLabelFor = (cat: (typeof categories)[number]): string | null => {
    const hint = describeUnit(cat);
    if (hint === null) return null;
    return hint.kind === "fits"
      ? tn("Fits 1 person", "Fits {n} people", hint.seats, { n: hint.seats })
      : tn("1 person each", "{n} people each", hint.seats, { n: hint.seats });
  };

  // The package minimum is intentionally NOT surfaced in the booking widget or
  // the traveller dropdown — it is a package-card concern. The card shows "This
  // package requires a minimum of N participants." and disables Book until met,
  // so the picker stays freely editable.

  // The default category (operator-flagged isDefault) drives the initial
  // selection and is pinned at minimum count 1. Falls back to the first
  // category in the package — the server is expected to guarantee exactly
  // one isDefault per package (Phase 3 invariant), so the fallback is only
  // there for legacy/incomplete data.
  const defaultCatId =
    categories.find((c) => c.isDefault)?.id ?? categories[0]?.id;

  // Pin the default category (e.g. "Adult") to the top of every user-facing
  // list — picker popover and price breakdown — so travelers always see the
  // primary count first. Other categories keep their original order.
  const orderedCategories = (() => {
    if (!defaultCatId) return categories;
    const idx = categories.findIndex((c) => c.id === defaultCatId);
    if (idx <= 0) return categories;
    const def = categories[idx]!;
    return [def, ...categories.slice(0, idx), ...categories.slice(idx + 1)];
  })();
  const showInlinePicker = orderedCategories.length <= INLINE_CATEGORY_LIMIT;
  // With one package there is no doubt whose price a row is, so each row
  // carries its own. With several, the package cards below price them.
  const showRowPrices = packages?.length === 1;

  // The collapsed trigger's one-liner, and the itemised block behind it. The
  // block carries EVERY picked category (the line shows two) and each one's
  // real head count, which is what the line has no room to say.
  const activeCategories = orderedCategories.filter(
    (c) => (travelers[c.id] ?? 0) > 0,
  );
  const breakdownText = (() => {
    const visible = activeCategories.slice(0, 2);
    const rest = activeCategories.length - visible.length;
    const head = visible
      // `×` (U+00D7), not the letter x — the guide's trigger reads "Adult × 1"
      // and the popover beside it already uses the real sign, so the letter
      // form made one control spell the same idea two ways.
      .map((c) => `${c.name || c.displayLabel} × ${travelers[c.id]}`)
      .join(", ");
    return rest > 0 ? `${head} ${t("+{n} More", { n: rest })}` : head;
  })();

  // The bands behind the headline price. Same category the headline is about —
  // the operator's default, skipping whole-group fees the way the calendar's
  // `defaultCategoryOf` does, because a group fee is one charge for the party
  // and has no "per person" ladder to show.
  //
  // `strategy`, not just a populated `tiers` array: switching a category back
  // to flat leaves its old bands on the row, so the array alone would keep
  // advertising a discount the operator has turned off.
  //
  // The category comes back WITH the bands. The panel has to match the party
  // against the same quantity the bands are expressed in — which is this one
  // category's own count, in this category's own unit — because that is what
  // `getPriceForCount` matches on. Returning only the bands is what let the
  // caller reach for the party's total head-count instead.
  const tierPanel = (() => {
    const perTraveler = categories.filter((c) => c.chargeBase !== "group");
    const cat = perTraveler.find((c) => c.isDefault) ?? perTraveler[0];
    if (!cat || cat.strategy !== "tiered" || !cat.tiers?.length) return null;
    return {
      cat,
      bands: cat.tiers
        .slice()
        .sort((a, b) => a.from - b.from)
        .map((band) => ({
          from: band.from,
          to: band.to,
          price: formatMoney(band.price.amount),
        })),
    };
  })();
  const tierBands = tierPanel?.bands ?? [];
  // The charge BASIS decides the noun, not the capacity — `tierUnitNoun`'s own
  // contract ("a per-person category always counts people, whatever its
  // display label says"), and the same key `_package-card.tsx` uses. A
  // capacity test disagreed with both on a `unit` category that carries no
  // `unitCapacity`, which would have read as "Travelers" over unit prices.
  const tierUnitIsPerson = tierPanel?.cat.chargeBase === "person";
  const tierUnitLabel = tierPanel
    ? tierPanel.cat.name || tierPanel.cat.displayLabel
    : "";

  const summaryRows: TravelerSummaryRow[] = activeCategories.map((c) => ({
    id: c.id,
    label: c.name || c.displayLabel,
    count: travelers[c.id] ?? 0,
    pax: paxFor(c, travelers[c.id] ?? 0),
  }));

  // Per-row "+" gate. A per-group category runs out of room one whole step
  // BEFORE the cap is reached (7 Couples = 14 of 15 heads, and the 8th is 2),
  // so "total < cap" cannot drive it.
  const canAddOneOf = (cat: (typeof categories)[number]): boolean =>
    canIncrementCategory({
      travelers,
      categories,
      categoryId: cat.id,
      pickerCap,
      unitCaps,
    });

  // The party-wide half of the same question, asked ONCE. When the party fills
  // the cap, `stepBlockedBy` refuses every row for the identical reason, so the
  // picker used to print the identical sentence on every row — three categories
  // meant three copies of "Your party is already at this tour's limit" under a
  // header that had just stated the cap. Four statements of one fact read as
  // four faults. The note above the list carries it instead, and each refused
  // "+" points at that note via `aria-describedby`.
  //
  // `totalTravelers`, not a second `sumPax` call. The two are provably equal —
  // `resolveBookingPricing` returns `totals: null` ONLY on
  // `categories.length === 0` and `headCount` is `sumPax(categories, travelers)`
  // otherwise, so the `?? 0` branch and `sumPax([], travelers)` are the same
  // zero — but the note at `totalTravelers` is right that two places deriving
  // one number is the shape of every pricing bug this page has shipped, and
  // this was a third. `stepBlockedBy` measures the rows with `sumPax` over the
  // same inputs, so the note and the rows still cannot disagree about the cap.
  const partyAtCap = pickerCap !== null && totalTravelers >= pickerCap;
  const partyOverCap = pickerCap !== null && totalTravelers > pickerCap;
  const partyCapNoteId = useId();

  // Naming a category is only honest when there is exactly one to name. The cap
  // is a single seat pool every category draws from (`stepBlockedBy` measures
  // `pickerCap - sumPax(ALL categories)`), so no category owns a limit of its
  // own: on an Adult + Child tour, ten Adults make "Child limit reached" just as
  // true, and just as meaningless. With one category the two statements describe
  // the same event, and the traveller reads the name they are looking at.
  const soleCategoryName =
    orderedCategories.length === 1
      ? orderedCategories[0]!.name || orderedCategories[0]!.displayLabel
      : null;

  // One sentence, three states — see the note where it renders. Built here
  // rather than inline because the category-name fork doubles each arm, and six
  // nested ternaries in JSX is where this stops being readable.
  const partyCapNote = (() => {
    if (pickerCap === null) return null;
    if (partyOverCap)
      return soleCategoryName
        ? t("Over the {category} limit of {n}.", {
            category: soleCategoryName,
            n: pickerCap,
          })
        : tn(
            "Over this tour's limit of 1 traveler.",
            "Over this tour's limit of {n} travelers.",
            pickerCap,
            { n: pickerCap },
          );
    if (partyAtCap)
      return soleCategoryName
        ? // Carries the number, like every other arm of this sentence. Without
          // it the traveller is told they have hit a limit but not what the
          // limit IS, so there is nothing to plan a smaller party around —
          // and the over-limit arm directly above does say it.
          tn(
            "{category} limit reached — 1 traveler.",
            "{category} limit reached — {n} travelers.",
            pickerCap,
            { category: soleCategoryName, n: pickerCap },
          )
        : tn(
            "Tour limit reached — 1 traveler.",
            "Tour limit reached — {n} travelers.",
            pickerCap,
            { n: pickerCap },
          );
    return tn(
      "Select up to 1 traveler in total.",
      "Select up to {n} travelers in total.",
      pickerCap,
      { n: pickerCap },
    );
  })();

  const updateTraveler = (categoryId: string, increment: number) => {
    // The picker is freely editable and clamps only on hard limits (count >= 0,
    // total within the capacity cap, party stays >= 1). Per-package/per-category
    // minimums are NOT a picker concern — the package card surfaces "requires a
    // minimum of N participants" and disables Book. Enforcing the minimum as a
    // picker floor previously froze a category whose default sat below it.
    setTravelers((prev) =>
      reduceTravelerUpdate({
        travelers: prev,
        categories,
        categoryId,
        increment,
        pickerCap,
        unitCaps,
      }),
    );
  };

  // Math is in minor units throughout (shared with the API). Convert to
  // dollars only at the formatMoney call site below — never re-introduce
  // mixed-unit arithmetic.
  const currentTotalMinor = totals?.totalMinor ?? 0;
  const originalTotalMinor = totals?.originalTotalMinor ?? 0;
  const totalSavingsMinor = originalTotalMinor - currentTotalMinor;

  // `.top` (the price) renders nothing at all on an on-request tour, or one
  // with no traveler categories — its one remaining child is gated on the
  // exact same condition below. The flags moved out to the ribbon above
  // `.card` and are no longer part of this check. An empty `.top` still
  // carries its own padding and sits above `.body`'s border-top, which
  // otherwise draws a rule separating the price from the controls with no
  // price above it to separate.
  const hasTopContent = pricingMode !== "onrequest" && categories.length > 0;

  // The two flags — independent of `hasTopContent` entirely now. This gates
  // the merch RIBBON, which straddles the card's top border (guide
  // `.bk-flags`) rather than sitting inside `.top`'s padding, so a badge or a
  // saving shows even on an on-request tour with no price to display.
  const hasFlags =
    Boolean(badge) || (pricingMode !== "onrequest" && totalSavingsMinor > 0);

  // Pure, and shared with the package card — `_price-presentation.ts` explains
  // why this is an OR of two counts, which one the guide never needed, and why
  // the zero-pax row the second arm catches is live rather than hypothetical.
  const showsPartyTotal = totals?.isPartyTotal ?? false;

  // Detect the "operator hasn't finished setup" state. Pricing without at
  // least one traveler category OR availability that resolved to an empty
  // array means we have nothing to compose a booking with. The
  // `departures !== null` guard avoids flashing the empty state during the
  // initial SSR-to-client handoff when departures haven't loaded yet.
  // `pricingMode === "onrequest"` skips this branch because the operator
  // explicitly opted into the quote flow above.
  const { hasNoPricing, hasNoAvailability, isNotConfigured } =
    resolveBookingConfigState({
      packages,
      selectedPackageId,
      pricingMode,
      departures,
      hasMoreAfter,
    });

  if (isNotConfigured) {
    return (
      <>
        <NotConfiguredCard
          hasNoPricing={hasNoPricing}
          hasNoAvailability={hasNoAvailability}
          onAskQuestion={() => setIsQuestionDialogOpen(true)}
        />
        <QuestionDialog
          open={isQuestionDialogOpen}
          onOpenChange={setIsQuestionDialogOpen}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          experienceSlug={experienceSlug}
          experienceName={experienceName}
          durationDays={durationDays}
          askQuestionHeading={askQuestionHeading}
        />
      </>
    );
  }

  // The category rows and the party-limit note. Shown straight in the rail
  // when the tour sells few categories, and inside the picker otherwise.
  // One sentence, three states. Below the cap it is advice and stays plain; at
  // or past it, it becomes the reason every "+" below has stopped responding,
  // and takes the destructive callout. The tint is conditional for exactly
  // that reason — "Select up to 10 travelers in total." is an invitation, and
  // a red box around an invitation reads as a fault the traveller has not
  // caused.  Still ONE element, not one per row: at the cap every category is
  // refused by the same seat pool, and each refused "+" points here through
  // `aria-describedby` instead of repeating the sentence.
  const capNote =
    partyCapNote !== null ? (
      partyAtCap ? (
        <Alert
          id={partyCapNoteId}
          variant="destructive"
          placement="compact"
          className={styles.pickerCapAlert}
        >
          {/* `AlertDescription`, not a bare string: the Alert is
                a two-column grid (icon, body) and an unwrapped text
                node auto-places into the icon column, which is 0
                wide with no icon — it rendered one word per line.
                The kit's body slots carry `grid-column-start: 2`.
                Description rather than Title because Title clamps to
                one line, and the multi-category sentence wraps to
                two in a 300px rail. */}
          <AlertDescription>{partyCapNote}</AlertDescription>
        </Alert>
      ) : (
        <p id={partyCapNoteId} className={styles.pickerCap}>
          {partyCapNote}
        </p>
      )
    ) : null;

  const rowPrice = (
    cat: (typeof categories)[number],
    count: number,
  ): string | undefined => {
    if (!showRowPrices) return undefined;
    const minor = rowUnitPriceMinor(cat, count);
    return minor === null ? undefined : formatMoney(minor);
  };

  // A rental at what its resource has left on the picked date says so on its
  // own row: the limit is this item's, not the party's.
  const unitCapReason = (categoryId: string, count: number) => {
    const cap = unitCaps[categoryId];
    if (cap === undefined || count < cap) return undefined;
    return cap === 0
      ? t("None left on this date")
      : tn("Only 1 left on this date", "Only {n} left on this date", cap, {
          n: cap,
        });
  };

  const renderPicker = (layout: "menu" | "inline") => (
    <>
      {capNote}
      <div className={styles.pickerRows}>
        {orderedCategories.map((cat) => {
          // Disable "−" only when removing one step of this category
          // (which frees `unitCapacity` heads) would empty the whole
          // party; otherwise any category can go to 0. There is no
          // per-category minimum floor here — the package card
          // validates package minimums.
          const count = travelers[cat.id] ?? 0;
          const min = decrementFloor({ cat, categories, travelers });
          // A dead "+" with no explanation reads as a broken button.
          // A Couple needs 2 seats, so on a 1-seat departure its "+"
          // is disabled the instant the picker opens (QA ef4e80e0).
          const blocked = stepBlockedBy({
            cat,
            categories,
            travelers,
            pickerCap,
          });
          return (
            <TravelerRow
              key={cat.id}
              title={cat.name || cat.displayLabel}
              subtitle={formatAgeRange(cat.ageGroup) ?? undefined}
              description={cat.description?.trim() || undefined}
              unitLabel={unitLabelFor(cat) ?? undefined}
              price={rowPrice(cat, count)}
              count={travelers[cat.id] ?? 0}
              onIncrement={() => updateTraveler(cat.id, 1)}
              onDecrement={() => updateTraveler(cat.id, -1)}
              min={min}
              canIncrement={canAddOneOf(cat)}
              // Row-specific only. `stepBlockedBy` refuses a step
              // for one of two kinds of reason, and they do not
              // belong in the same place: with seats still left in
              // the party, THIS category is the one that does not
              // fit (a Cabin wants 4, two are free) and only the row
              // can say so. At zero, every row is refused by the
              // same party-wide cap, which the note above the list
              // now states once — so the row says nothing and
              // `describedBy` points its "+" at that note.
              //
              // `seatsLeft === 0` covers both party-wide arms: the
              // helper clamps a negative remainder to zero, so an
              // over-committed departure and an exactly-full one
              // both land here, as does a flat group fee (needs 0),
              // which only ever fails once the party is already
              // past the cap.
              blockedReason={
                unitCapReason(cat.id, count) ??
                (blocked && blocked.seatsLeft > 0
                  ? tn(
                      "Needs 1 more seat — your party has only {left} left",
                      "Needs {needs} more seats — your party has only {left} left",
                      blocked.needs,
                      {
                        needs: blocked.needs,
                        left: blocked.seatsLeft,
                      },
                    )
                  : undefined)
              }
              describedBy={partyAtCap ? partyCapNoteId : undefined}
              layout={layout}
            />
          );
        })}
      </div>
    </>
  );
  return (
    // Guide `.bk` — the rail travels with the reader, so it takes a wider,
    // softer shadow than a card's tight contact shadow, and a firm edge that
    // keeps it distinct from whatever scrolls past underneath.
    // The large radius on the phone — the mobile guide's `--r-lg`, which every
    // card on that sheet carries. The medium one against the large one on the
    // departure cards directly below read as two families of card in one
    // section.
    <div className={cx(styles.wrapper, hasFlags && styles.wrapperFlagged)}>
      {/* Guide `.bk-flags` — the merchandising ribbon straddling the card's
          top border, same treatment as the package card's own ribbon (guide
          `.opt-flags`) so the two read as one vocabulary. Unclipped on
          purpose: `.card` is `overflow: hidden` for its rounded corners,
          which is exactly what would clip a ribbon hanging above its edge. */}
      {hasFlags && (
        <span className={styles.flags}>
          {/* The two flags are INDEPENDENT. Nesting the discount inside the
            badge check meant a tour with a real saving and no operator
            badge lost its "off" flag entirely — the strikethrough below
            still showed, but the thing that draws the eye did not. */}
          {badge ? (
            <MerchFlag tone="seller" className={styles.flag}>
              {badge}
            </MerchFlag>
          ) : null}
          {pricingMode !== "onrequest" && totalSavingsMinor > 0 ? (
            <MerchFlag
              tone="discount"
              className={styles.flag}
              // Guide `.flag-save` carries a mark; `.flag-seller` does
              // not. The asymmetry is deliberate — two identically
              // shaped pills side by side need something other than
              // colour to tell them apart at a glance.
              icon={<TicketPercent aria-hidden strokeWidth={2.2} />}
            >
              {t("{amount} off", {
                amount: formatMoney(totalSavingsMinor),
              })}
            </MerchFlag>
          ) : null}
        </span>
      )}
      <div className={styles.card}>
        {/* `.bk-top` — everything above the first rule: the price. Its own
          padding, because `.bk-body` below carries the controls and the two
          are separated by a rule rather than by space. Skipped entirely when
          there's nothing to show (hasTopContent) — an on-request tour, or one
          with no traveler categories, has no price to put here. */}
        {hasTopContent && (
          <div className={styles.top}>
            {/* AC-10: the surviving package's price has no place on an on-request
            tour page — the quote box below is the one source of "what this
            costs" while the mode is active. */}
            {pricingMode !== "onrequest" && categories.length > 0 && (
              <div className={styles.price}>
                <div>
                  {/* Guide `#railLabel` — a total is named WITH its head count:
                "Total price for 6 travellers", not a bare "Total price". The
                figure below changes with the party, so the label is what says
                which party it is for; without the count the reader has to
                remember what they set two fields down. */}
                  <span className={styles.priceLabel}>
                    {showsPartyTotal
                      ? tn(
                          "Total price for 1 traveler",
                          "Total price for {n} travelers",
                          totalTravelers,
                          { n: totalTravelers },
                        )
                      : t("Starting from")}
                  </span>
                </div>
                <div className={styles.priceRow}>
                  {/* Guide `.bk-amt` + `.bk-cur` — the currency sits a type step
                down (22px against 32px) and a shade quieter. At 32px a symbol
                set in the figure's own size eats a third of its width for
                information the reader already has. */}
                  <PriceFigure
                    formatted={formatMoney(currentTotalMinor)}
                    className={styles.amount}
                    affixClassName={styles.amountAffix}
                  />
                  {/* Muted, not disabled. The guide greys a struck price with
                `--disabled`, but that tone clears NO contrast floor (2.53:1
                measured on this panel) and this is a price someone reads to
                work out what they are saving. A strikethrough already says
                "superseded"; it does not need an illegible colour to say it
                twice. */}
                  {totalSavingsMinor > 0 && (
                    <s className={styles.wasPrice}>
                      {formatMoney(originalTotalMinor)}
                    </s>
                  )}
                  <div className={styles.priceActions}>
                    {showsPartyTotal && (
                      <LazyPopover
                        open={isPriceDetailsOpen}
                        onOpenChange={setIsPriceDetailsOpen}
                        align="start"
                        renderTrigger={(triggerProps) => (
                          <Button
                            variant="link"
                            size="sm"
                            // Guide `.bk-pd` — 12.5px semibold with a 1px rule at
                            // 20% brand, matching `.bk-tier` beneath it. At 14px
                            // bold with a 2px underline it carried more weight than
                            // the struck price it sits beside, and read as a second
                            // action next to the figure rather than a way to look
                            // behind it.
                            className={styles.priceDetailsTrigger}
                            {...triggerProps}
                          >
                            {t("Price Details")}
                          </Button>
                        )}
                      >
                        <div className={styles.priceDetailsList}>
                          {orderedCategories.map((cat) => {
                            const count = travelers[cat.id] ?? 0;
                            if (count <= 0) return null;
                            const unitPriceMinor =
                              resolveCategoryUnitPriceMinor(cat, count);
                            return (
                              <div
                                key={cat.id}
                                className={styles.priceDetailsLine}
                              >
                                <span className={styles.priceDetailsDesc}>
                                  {cat.name ?? cat.displayLabel} &times; {count}
                                </span>
                                <span className={styles.priceDetailsTotal}>
                                  {formatMoney(count * unitPriceMinor)}
                                </span>
                              </div>
                            );
                          })}
                          {/* One lump saving, not a per-category figure — the
                          headline's struck price above is already the same
                          single comparison (current vs. original total), and
                          splitting it per row would invite the reader to add
                          categories that each round differently than the
                          total does. */}
                          {totalSavingsMinor > 0 && (
                            <div
                              className={cx(
                                styles.priceDetailsLine,
                                styles.priceDetailsSavings,
                              )}
                            >
                              <span>{t("Savings")}</span>
                              <span>-{formatMoney(totalSavingsMinor)}</span>
                            </div>
                          )}
                          <div
                            className={cx(
                              styles.priceDetailsLine,
                              styles.priceDetailsGrandTotal,
                            )}
                          >
                            <span>{t("Total")}</span>
                            <span>{formatMoney(currentTotalMinor)}</span>
                          </div>
                        </div>
                      </LazyPopover>
                    )}
                  </div>
                </div>

                {/* Guide `.bk-per` — the unit sits on its own line UNDER the price,
              not inline beside the struck-through one. Inline, it queued up
              behind two numbers and a strikethrough on the same baseline and
              was the first thing to wrap on a narrow rail, landing "Adult" on
              a line of its own anyway — just an unintended one. */}
                {(() => {
                  // Guide `#railPer`, and the RULE is the load-bearing part:
                  //
                  //   one traveller      the charge basis alone, no figure
                  //   2+, one category   "{rate} {basis}"
                  //   2+, MIXED          nothing at all
                  //
                  // The third case is not an omission. The guide hides the line
                  // with `hidden = !!(state.adult && state.youth)` and says why:
                  // "two rates in one basket, so a single per-person figure would
                  // mislead". Quoting one category's rate over a mixed party states
                  // a price that is true of nobody in it, and quoting the average
                  // invents a number no one is charged. There is no honest single
                  // figure, so there is no line.
                  const selected = categories.filter(
                    (c) => (travelers[c.id] ?? 0) > 0,
                  );

                  // The ORDER of these three answers is the load-bearing part, and
                  // it lives in `resolvePriceUnitLine` with its own tests — it
                  // regressed once by having `soldAs` return before the mixed-party
                  // guard, which printed "Per person twin share" over a basket
                  // holding two different rates.
                  //
                  // Keyed on how many CATEGORIES the traveller picked, never on
                  // head-count: a flat-group category contributes ZERO pax
                  // (`paxFor`), so Adult x1 + "Cleaning fee" x1 is two categories
                  // at a head count of one, and a head-count guard sits out exactly
                  // there — the line then quoted `selected[0]`, whichever category
                  // happens to sort first, so a package whose fee sorts first
                  // printed "per group" under a total that was an adult plus a fee.
                  const unitLine = resolvePriceUnitLine({
                    selectedCategoryCount: selected.length,
                    soldAs,
                  });
                  if (unitLine === "none") return null;
                  // `soldAs` is defined in the builder as "Displayed on tour cards
                  // next to the price", its placeholder is literally "Per person
                  // twin share", and the tour CARD already renders it as the unit
                  // label under the price. It was rendering here too — but ABOVE
                  // the figure, as a standalone 14px paragraph between the badge
                  // and the word "STARTING FROM", so a price suffix arrived before
                  // the price and the basis was then stated a second time below.
                  // The guide has one slot for this and it is this line: `.bk-per`
                  // reads "per person, twin-share".
                  if (unitLine === "sold-as") {
                    return <p className={styles.unitLine}>{soldAs}</p>;
                  }

                  const activeCat = selected[0] ?? categories[0];
                  if (!activeCat) return null;
                  // The CHARGE BASIS, never the category name. `mapApiCategory`
                  // sets `displayLabel: cat.displayLabel || cat.name`, so an
                  // operator who left the phrasing blank silently turns this into
                  // the name — which is how the line read "₨1,350.00 Adult": a rate
                  // per Adult-the-noun rather than per person. When the label is
                  // only the name, fall back to `chargeBase`, which is what the
                  // price-category modal's "charged as" actually stores.
                  const basis = chargeBasisLabel(activeCat, t);

                  const count = travelers[activeCat.id] ?? 0;
                  const rateMinor =
                    totalTravelers >= 2 && count > 0
                      ? resolveCategoryUnitPriceMinor(activeCat, count)
                      : null;
                  // NO BARE BASIS. A line reading only "per adult" repeats what the
                  // traveller picker directly below already states, and it is
                  // phrasing we derived rather than anything the operator wrote —
                  // which is what got reported. With a rate in front of it the same
                  // line earns its place: "₨1,350.00 per person" is the per-head
                  // number a party of two cannot read off the total.
                  //
                  // The operator's own `soldAs` still prints on its own, above.
                  //
                  // So there is NO unit line in the default one-traveller state,
                  // and it appears when a second is added. That is the rule, not a
                  // gap in it: with one traveller the headline price already IS the
                  // per-head price, and restating it underneath says nothing. The
                  // line costs a ~20px reflow when it arrives, which is accepted —
                  // it follows a tap the traveller just made, so it is not a
                  // layout-shift the page is charged for.
                  if (rateMinor == null) return null;
                  return (
                    // Guide `.bk-per` is 13px, a step under body text. At 14px it
                    // sat level with the controls below and read as a second
                    // statement rather than the price's own unit.
                    <p className={styles.unitLine}>
                      {`${formatMoney(rateMinor)} ${basis}`}
                    </p>
                  );
                })()}

                {/* Guide `.bk-rate` — five small stars, the average in bold, and a
              muted jump link to the reviews. A different treatment from the
              hero's single star on purpose: this one is the last reassurance
              before the CTA. Absent entirely when nobody has reviewed the
              tour, rather than showing an empty five-star frame. */}
                {rating && rating.count > 0 ? (
                  // 13px row with 14px stars — the guide's `.bk-rate` and `.rt-sm`.
                  // Ours had the two the wrong way round: a 14px row carrying 13px
                  // stars, so the glyphs sat a step UNDER the number they qualify
                  // instead of a step over it.
                  <div className={styles.rating}>
                    <StarRow average={rating.average} size="sm" />
                    <b className={styles.ratingValue}>
                      {rating.average.toFixed(1)}
                    </b>
                    {/* Guide `.lk-sm` — 12.5px semibold, a step under the 13px
                  row so the count reads as a link rather than as more of the
                  rating. It was inheriting the row outright (14px regular). */}
                    {/* Underlined and brand-coloured AT REST below `lg`. The
                    desktop treatment is a muted label that becomes a link on
                    hover — and a touch screen has no hover, so without this it
                    is simply a grey number nobody knows they can tap.
                    `lg`, not `sm`: this card is the inline mobile layout all
                    the way to 1024px, and at `sm` an 800px touch tablet was
                    handed the hover-only version on a screen that cannot
                    hover. One breakpoint for the whole route — see
                    `_use-is-desktop.client.ts`. */}
                    <a href="#reviews" className={styles.reviewsLink}>
                      {tn("({n} review)", "({n} reviews)", rating.count, {
                        // The agency's locale, not the runtime default. Thousands
                        // separators differ (1,200 / 1.200 / 1 200), and this is
                        // SSR'd — so on any tour with four-figure reviews the
                        // server and the browser could render different text and
                        // React would report a hydration mismatch.
                        n: rating.count.toLocaleString(localeTag),
                      })}
                    </a>
                  </div>
                ) : null}

                {/* Honest group-pricing hint — shown only when the experience
              actually prices a category by group size.

              Guide `.bk-tier`: brand-coloured, 12.5px/600, a 14px mark. TWO
              shapes on purpose. With real bands to show it is the guide's
              BUTTON, under a dashed rule that turns solid on hover, and it
              opens the tier table. With none — `hasGroupPricing` is true
              because some OTHER category is tiered, so there is no per-person
              ladder for the headline — it falls back to a plain statement,
              because a dashed rule under something that cannot be clicked
              advertises a control that is not there. */}
                {tierBands.length > 1 ? (
                  <LazyPopover
                    align="start"
                    sideOffset={8}
                    contentClassName={styles.tierPopover}
                    renderTrigger={(triggerProps) => (
                      <Button
                        variant="link"
                        size="sm"
                        // The PHONE takes the guide's `.bk-tier` box as well as its
                        // type: a full-width brand-tinted chip. Bare on a 358px rail
                        // the line read as a caption trailing the star row rather
                        // than as the third fact about this price.
                        className={styles.tierTrigger}
                        {...triggerProps}
                      >
                        <Users strokeWidth={2} aria-hidden />
                        {t("Group Size and Prices")}
                      </Button>
                    )}
                  >
                    {/* No heading and no caption, both of which the guide has.
                    Its `.pv-title` is gone by request, and the paragraph it
                    puts under the table explains the tier applies to the
                    booking rather than to the departure — which the unit
                    column already says. The trigger names the panel. */}
                    <GroupTierTable
                      bands={tierBands}
                      // This category's own count, not the party's head-count — see
                      // the prop's note. A per-unit category counts units.
                      headcount={
                        tierPanel ? (travelers[tierPanel.cat.id] ?? 0) : 0
                      }
                      unitNoun={
                        tierUnitIsPerson
                          ? t("Travelers")
                          : tierUnitNoun(tierUnitLabel, false)
                      }
                    />
                  </LazyPopover>
                ) : hasGroupPricing ? (
                  // No ladder to open, so this stays a statement — with the phone's
                  // chip box, which is main's mobile treatment and applies to both
                  // shapes.
                  //
                  // SC #176 renamed the TRIGGER above to "Group Size and Prices",
                  // which reads as a label on something you can open. This branch
                  // opens nothing, so the same words would name a topic and tell
                  // the traveller nothing — and it is the only informational line
                  // the branch has. It keeps a sentence, as the line above says.
                  //
                  // "VARIES", not "drops", and the same sentence the destination
                  // card already shows off this same `hasGroupPricing` flag
                  // (`destinations/[slug]/_tour-card.client.tsx`, whose comment
                  // sets the rule: the caption states a fact about THIS tour).
                  // With no ladder rendered here there is nothing on screen
                  // showing the price going DOWN, so claiming a direction is a
                  // promise this branch cannot show the traveller.
                  <p className={styles.tierNote}>
                    <Users
                      className={styles.tierNoteIcon}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className={styles.tierNoteText}>
                      {t("Price varies by group size")}
                    </span>
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* `.bk-body` — the controls. Separated from `.top` by a rule only when
          `.top` actually rendered something to separate from — otherwise the
          rule is a line above nothing. */}
        <div className={cx(styles.body, !hasTopContent && styles.bodyFlush)}>
          {pricingMode === "onrequest" ? (
            <>
              {/* AC-10: a package/departures that survived the switch to
                on-request still has real dates to browse — letting the
                traveler pick one (and see it on the departure-dates card
                below, price hidden there) is the only way `showPackages` in
                the parent view ever turns true for this tour. No `prices`
                map and `hasRoundedPrice={false}`: the calendar exists to show
                WHICH dates run, not what they would have cost. A tour with no
                surviving package has nothing to browse, so this is skipped
                and the quote box below is the whole story — unchanged from
                before this fix. */}
              {hasDepartureDates && (
                <>
                  <div className={styles.fields}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>
                        {t("Select Date")}
                      </label>
                      <DateField
                        open={isCalendarOpen}
                        onOpenChange={setIsCalendarOpen}
                        selectedDate={selectedDate}
                        onSelect={setSelectedDate}
                        departures={departures}
                        prices={undefined}
                        totalTravelers={totalTravelers}
                        numberOfMonths={calendarMonths}
                        onMonthChange={handleCalendarMonthChange}
                        hasRoundedPrice={false}
                        showError={showError}
                        formatDate={formatDate}
                        t={t}
                      />
                    </div>
                  </div>

                  {showError && (
                    <div className={styles.dateError}>
                      <AlertCircle />
                      {t("Please select a date first")}
                    </div>
                  )}

                  <div className={styles.ctaGroup}>
                    <Button
                      size="lg"
                      onClick={onCheckAvailability}
                      className={styles.checkAvailability}
                    >
                      {t("Check Availability")}
                    </Button>
                  </div>
                </>
              )}

              {onRequest?.showEstimatedPrice && onRequest.estimatedFrom && (
                <div className={styles.estimate}>
                  {/* The operator's own caption, falling back to ours. Both
                    captions and the struck-through original are authored in the
                    builder's quote box. */}
                  <span className={styles.estimateLabel}>
                    {onRequest.priceBeforeText || t("Estimated From")}
                  </span>
                  <div className={styles.estimateRow}>
                    {onRequest.originalPrice &&
                    onRequest.originalPrice.amount >
                      onRequest.estimatedFrom.amount ? (
                      <s className={styles.estimateWas}>
                        {formatMoney(onRequest.originalPrice.amount)}
                      </s>
                    ) : null}
                    <span className={styles.estimateAmount}>
                      {formatMoney(onRequest.estimatedFrom.amount)}
                    </span>
                    {onRequest.priceAfterText ? (
                      <span className={styles.estimateSuffix}>
                        {onRequest.priceAfterText}
                      </span>
                    ) : null}
                  </div>
                  <p className={styles.estimateNote}>
                    {t("Final price depends on group size and requirements")}
                  </p>
                </div>
              )}

              <div className={styles.quoteIntro}>
                <h3 className={styles.quoteHeading}>
                  {onRequest?.sectionTitle ||
                    onRequest?.formHeading ||
                    t("Plan your private trip")}
                </h3>
                {onRequest?.formMessage && (
                  <p className={styles.quoteMessage}>{onRequest.formMessage}</p>
                )}
              </div>

              {/* The box's selling points ARE this tour's Pricing Highlights —
                not a quote-box-local list (AC-13). */}
              {pricingHighlights && pricingHighlights.length > 0 ? (
                <ul className={styles.quoteHighlights}>
                  {pricingHighlights.map((text, i) => (
                    <li key={i}>
                      <CircleCheck aria-hidden strokeWidth={1.75} />
                      {text}
                    </li>
                  ))}
                </ul>
              ) : null}

              {onRequest && (
                <div className={styles.response}>
                  <div className={styles.responseIcon}>
                    <Clock strokeWidth={3} />
                  </div>
                  <p className={styles.responseText}>
                    {t("Typical response within {time} {unit}", {
                      time: onRequest.responseTime,
                      unit: onRequest.responseUnit,
                    })}
                  </p>
                </div>
              )}

              <div className={styles.quoteActions}>
                {(() => {
                  const label = onRequest?.buttonLabel || t("Request a Quote");
                  // The guide paints every booking commit in `--cta`, which
                  // traveler-ui carries behind `<Button variant="book">`. An
                  // on-request quote IS the commit on a tour that has no instant
                  // checkout, so it wears the same colour as "Book now"
                  // everywhere else on the page.
                  const ctaClass = styles.cta;
                  // A custom link wins over the enquiry destination: it is the
                  // newer control and the operator set it explicitly. `noopener`
                  // is not optional on a `_blank` link — without it the opened
                  // page can reach back through `window.opener`.
                  if (onRequest?.linkMode === "url" && onRequest.linkUrl) {
                    return (
                      <Button
                        size="lg"
                        variant="book"
                        className={ctaClass}
                        render={
                          <a
                            className={styles.ctaLink}
                            href={onRequest.linkUrl}
                            {...(onRequest.linkOpensNewTab
                              ? { target: "_blank", rel: "noopener noreferrer" }
                              : {})}
                          />
                        }
                      >
                        <ExternalLink />
                        {label}
                      </Button>
                    );
                  }
                  if (onRequest?.buttonAction === "email") {
                    return (
                      <Button
                        size="lg"
                        variant="book"
                        className={ctaClass}
                        render={
                          <a
                            className={styles.ctaLink}
                            href={`mailto:${onRequest.contactEmail}`}
                          />
                        }
                      >
                        <Mail />
                        {label}
                      </Button>
                    );
                  }
                  if (onRequest?.buttonAction === "phone") {
                    return (
                      <Button
                        size="lg"
                        variant="book"
                        className={ctaClass}
                        render={
                          <a
                            className={styles.ctaLink}
                            href={`tel:${onRequest.contactPhone}`}
                          />
                        }
                      >
                        <Phone />
                        {label}
                      </Button>
                    );
                  }
                  if (onRequest?.buttonAction === "whatsapp") {
                    return (
                      <Button
                        size="lg"
                        variant="book"
                        className={ctaClass}
                        render={
                          <a
                            className={styles.ctaLink}
                            href={`https://wa.me/${onRequest.whatsapp.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        }
                      >
                        <MessageCircle />
                        {label}
                      </Button>
                    );
                  }
                  // "Open inquiry form": the operator's chosen Leads form,
                  // already resolved server-side. `requestedForm` is `null`
                  // both for a tour that never picked one and for a picked
                  // form since deleted — either way there is nothing to open,
                  // so this states that plainly rather than silently falling
                  // back to the generic Ask a Question form the operator did
                  // not choose (AC-14).
                  if (!onRequest?.requestedForm) {
                    return (
                      <Button
                        size="lg"
                        variant="book"
                        disabled
                        className={ctaClass}
                      >
                        <Send />
                        {t("Not available right now")}
                      </Button>
                    );
                  }
                  return (
                    <Button
                      size="lg"
                      variant="book"
                      onClick={() => setIsQuoteFormDialogOpen(true)}
                      onMouseEnter={prefetchQuestionDialog}
                      onFocus={prefetchQuestionDialog}
                      className={ctaClass}
                    >
                      <Send />
                      {label}
                    </Button>
                  );
                })()}
              </div>
            </>
          ) : (
            <>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>{t("Travelers")}</label>
                  {showInlinePicker ? (
                    <div className={styles.inlinePicker}>
                      {renderPicker("inline")}
                    </div>
                  ) : (
                    <>
                      {/* A SHEET on a phone, the anchored popover from `lg` — the
                      same split the date field uses, via the same component. It
                      was a popover on every viewport, so the two fields stacked
                      in this card opened two different ways on the same tap; the
                      mobile guide opens both as `.sheet`. */}
                      <FieldOverlay
                        open={isTravelersOpen}
                        onOpenChange={setIsTravelersOpen}
                        title={t("Travelers")}
                        doneLabel={t("Apply")}
                        popover={{
                          align: "start",
                          // Guide `.tvd-pop` — a 10px radius, not the 20px card. It
                          // is a field's own menu, sitting 6px under an 8px-radius
                          // trigger; a 20px corner made it read as a separate card
                          // that had landed on top of the rail.
                          contentClassName: styles.travelersPopover,
                          // Base UI's default lets a popup fall back to the
                          // perpendicular axis (left/right) when it doesn't fit
                          // above or below — correct for a context menu, wrong
                          // for a labelled form field: it reads as broken when
                          // the menu lands beside the card instead of under its
                          // own trigger. `fallbackAxisSide: "none"` keeps the
                          // flip to top/bottom only.
                          collisionAvoidance: { fallbackAxisSide: "none" },
                        }}
                        renderTrigger={(popoverProps) => (
                          // On the field itself, not on the breakdown text inside
                          // it: a span is not a tab stop, so the itemised party was
                          // hover-only. Always mounted, so the field the popover is
                          // anchored to never remounts when the party empties.
                          <LazyTooltip
                            contentClassName={styles.summaryTooltip}
                            content={
                              <TravelersSummary
                                rows={summaryRows}
                                total={totalTravelers}
                              />
                            }
                            renderTrigger={(tooltipProps) => (
                              <Button
                                variant="field"
                                size="lg"
                                {...mergeTriggerProps(
                                  tooltipProps,
                                  popoverProps,
                                )}
                                // The popover branch supplies its own `onClick` through
                                // `popoverProps`; the sheet branch has no anchor to
                                // attach to, so it opens here. Same shape as
                                // `DateTrigger`.
                                {...(popoverProps
                                  ? {}
                                  : {
                                      onClick: () => setIsTravelersOpen(true),
                                    })}
                              >
                                <div className={styles.fieldValue}>
                                  <Users className={styles.fieldIcon} />
                                  {/* The party size is the fact the traveller is checking;
                                the per-category breakdown is supporting detail. So
                                the total is pinned and never truncates, and the
                                breakdown gives up its width first — previously they
                                shared one truncating line and the total was the part
                                that got cut off. */}
                                  <span className={styles.breakdown}>
                                    {breakdownText}
                                  </span>
                                  {totalTravelers > 0 && (
                                    <span className={styles.partyTotal}>
                                      {tn(
                                        "1 traveler",
                                        "{n} travelers",
                                        totalTravelers,
                                        {
                                          n: totalTravelers,
                                        },
                                      )}
                                    </span>
                                  )}
                                </div>
                                <ChevronDown className={styles.fieldIcon} />
                              </Button>
                            )}
                          />
                        )}
                      >
                        {renderPicker("menu")}
                        {/* DESKTOP ONLY. On a phone this control is the sheet's own
                      full-width footer (`FieldOverlay`), sized for a thumb and
                      pinned below the scrolling body; rendering both would put
                      two Apply buttons in one sheet. `hidden` keeps the spare
                      one out of the accessibility tree as well as out of sight.
                      Same word in both places, so the field does not rename its
                      own button at 1024px. */}
                        <div className={styles.pickerFooter}>
                          {/* Guide `.tvd-foot`: `tb tb-sm tb-outline` — a small
                        OUTLINE button, not a filled primary. The counts are
                        already committed (`updateTraveler` writes every +/-
                        straight to state), so this only dismisses the menu; a
                        solid brand fill gave the popover's least consequential
                        control the same weight as "Check Availability" in the
                        card behind it.
                        The colour comes from the variant, not a className. */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsTravelersOpen(false)}
                          >
                            {t("Apply")}
                          </Button>
                        </div>
                      </FieldOverlay>
                    </>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    {t("Select Date")}
                  </label>
                  <DateField
                    open={isCalendarOpen}
                    onOpenChange={setIsCalendarOpen}
                    selectedDate={selectedDate}
                    onSelect={setSelectedDate}
                    departures={departures}
                    prices={calendarPrices}
                    totalTravelers={totalTravelers}
                    numberOfMonths={calendarMonths}
                    onMonthChange={handleCalendarMonthChange}
                    hasRoundedPrice={hasRoundedPrice}
                    showError={showError}
                    formatDate={formatDate}
                    t={t}
                  />
                </div>

                {showError && (
                  <div className={styles.dateError}>
                    <AlertCircle />
                    {t("Please select a date first")}
                  </div>
                )}
              </div>

              {/* Guide `.bk-cta` — 9px between the two actions, not 12. They are
              one decision offered two ways, so they sit closer than the 12px
              rhythm the fields above use to separate different questions. */}
              <div className={styles.ctaGroup}>
                {/* THE TENANT'S OWN PRIMARY, not the guide's amber. This CTA was
                  briefly `variant="book"` on the argument that one page should
                  have one commit colour; the operator's call is that the rail's
                  headline action wears the agency's brand, and it is their
                  brand to spend. The amber stays on the controls that actually
                  commit to a date — the departure rows, the package cards and
                  the pinned bar — where it reads as the shop's own accent
                  rather than as the site's primary. */}
                <Button
                  size="lg"
                  onClick={onCheckAvailability}
                  // The tenant's primary with a translucent hover, spelled out in
                  // the module so the operator's brand stays on the biggest
                  // control on their tour page.
                  className={styles.checkAvailability}
                >
                  {t("Check Availability")}
                </Button>

                <AskButton
                  onClick={() => setIsQuestionDialogOpen(true)}
                  onMouseEnter={prefetchQuestionDialog}
                  onFocus={prefetchQuestionDialog}
                >
                  <MessageCircle className={styles.askIcon} />
                  {t("Ask a Question")}
                </AskButton>
              </div>
            </>
          )}

          {/* Guide `.bk-trust` — the included list belongs UNDER the actions, not
          above them. The guide's foot is one block: `.bk-cta` and then the
          reassurance, so the reader meets the controls first and the small
          print after. This sat at the TOP of `.bk-body` instead, which pushed
          Travelers and Select date down the rail behind a list nobody has to
          read to book, and put a second heading ("What's included") in front of
          the thing the card exists to do. */}
          {/* An on-request tour already shows these inside the quote box above
            (AC-13) — rendering them again here would repeat the same list
            twice on one card. */}
          {pricingMode !== "onrequest" &&
          pricingHighlights &&
          pricingHighlights.length > 0 ? (
            <div className={styles.included}>
              {/* No label over the list: a row of green checks already reads as
              "included", and the heading competed with the card's own one. */}
              <ul className={styles.includedList}>
                {pricingHighlights.map((highlight, i) => (
                  <li key={i} className={styles.includedItem}>
                    <CircleCheck
                      className={styles.includedIcon}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Payment terms, stated from the operator's own policy. The previous
          block hardcoded "pay nothing today" and a 2-day balance deadline,
          which contradicted any experience configured with a deposit. Nothing
          renders when no policy is set — same reasoning as the hardcoded
          free-cancellation badge removed from here earlier (it contradicted
          /terms and had no field to drive it). */}
          {/* Guide `.bk-trust` — sits below a rule at the foot of the body, so the
          terms read as the small print under the action rather than as more
          content competing with it. */}
          {payment ? (
            <div className={styles.payment}>
              {payment.depositPercent > 0 ? (
                <Trust
                  label={t("Pay {pct}% today", { pct: payment.depositPercent })}
                  body={t("Balance due {n} days before departure.", {
                    n: payment.balanceDueDaysBefore,
                  })}
                />
              ) : (
                <Trust
                  label={t("Reserve now & pay later")}
                  body={t(
                    "Pay nothing today — full balance due {n} days before departure.",
                    {
                      n: payment.balanceDueDaysBefore,
                    },
                  )}
                />
              )}
            </div>
          ) : null}
        </div>

        <QuestionDialog
          open={isQuestionDialogOpen}
          onOpenChange={setIsQuestionDialogOpen}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          experienceSlug={experienceSlug}
          experienceName={experienceName}
          durationDays={durationDays}
          askQuestionHeading={askQuestionHeading}
        />
        {onRequest?.requestedForm ? (
          <QuestionDialog
            open={isQuoteFormDialogOpen}
            onOpenChange={setIsQuoteFormDialogOpen}
            isSubmitting={isQuoteFormSubmitting}
            setIsSubmitting={setIsQuoteFormSubmitting}
            isSubmitted={isQuoteFormSubmitted}
            setIsSubmitted={setIsQuoteFormSubmitted}
            experienceSlug={experienceSlug}
            experienceName={experienceName}
            durationDays={durationDays}
            askQuestionHeading={
              onRequest.sectionTitle ||
              onRequest.formHeading ||
              t("Plan your private trip")
            }
            form={onRequest.requestedForm}
          />
        ) : null}
      </div>
    </div>
  );
}

/** The 24-hour start times the package runs on the picked date. */
function startTimesOn(
  departures: readonly DepartureDto[] | null,
  date: Date | undefined,
  packageId: string | undefined,
): string[] {
  if (!departures || !date || !packageId) return [];
  const day = localDateStr(date);
  const times = departures.flatMap((d) =>
    d.date.slice(0, 10) === day && d.packageId === packageId && d.startTime
      ? [to24h(d.startTime)]
      : [],
  );
  return [...new Set(times)].sort();
}
